import CategoryCard from "./CategoryCard";
import { FileText, Github, Briefcase, TrendingUp } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useState, useMemo } from "react";

const Categories = () => {
  const { t, language } = useLanguage();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Optimization: Memoize categories array to provide stable props to MemoizedCategoryCard
  // This prevents all cards from re-rendering when the parent re-renders due to hover state changes
  const categories = useMemo(() => [
    {
      title: t("categories.articles.title"),
      description: t("categories.articles.description"),
      icon: FileText,
      delay: 0,
      link: "/blog",
      equationWord: language === "de" ? "Wissen" : "Knowledge",
      color: "text-blue-500",
    },
    {
      title: t("categories.startups.title"),
      description: t("categories.startups.description"),
      icon: TrendingUp,
      delay: 100,
      link: "/perspectives",
      equationWord: language === "de" ? "Perspektiven" : "Perspectives",
      color: "text-purple-500",
    },
    {
      title: t("categories.tools.title"),
      description: t("categories.tools.description"),
      icon: Github,
      delay: 200,
      link: "/tools",
      equationWord: language === "de" ? "Handlung" : "Action",
      color: "text-green-500",
    },
    {
      title: t("categories.caseStudies.title"),
      description: t("categories.caseStudies.description"),
      icon: Briefcase,
      delay: 300,
      link: "/case-studies",
      equationWord: language === "de" ? "Erfolg" : "Success",
      color: "text-amber-500",
    },
  ], [t, language]);

  const renderEquation = () => {
    if (language === "de") {
      return (
        <span className="inline-flex flex-wrap items-center justify-center gap-2 text-xl">
          <span className={`transition-all duration-300 ${hoveredIndex === 0 ? `${categories[0].color} font-bold scale-110` : 'text-muted-foreground'}`}>
            Wissen
          </span>
          <span className="text-muted-foreground">×</span>
          <span className={`transition-all duration-300 ${hoveredIndex === 1 ? `${categories[1].color} font-bold scale-110` : 'text-muted-foreground'}`}>
            Perspektiven
          </span>
          <span className="text-muted-foreground">×</span>
          <span className={`transition-all duration-300 ${hoveredIndex === 2 ? `${categories[2].color} font-bold scale-110` : 'text-muted-foreground'}`}>
            Handlung
          </span>
          <span className="text-muted-foreground">=</span>
          <span className={`transition-all duration-300 ${hoveredIndex === 3 ? `${categories[3].color} font-bold scale-110` : 'text-muted-foreground'}`}>
            Erfolg
          </span>
        </span>
      );
    }

    return (
      <span className="inline-flex flex-wrap items-center justify-center gap-2 text-xl">
        <span className={`transition-all duration-300 ${hoveredIndex === 0 ? `${categories[0].color} font-bold scale-110` : 'text-muted-foreground'}`}>
          Knowledge
        </span>
        <span className="text-muted-foreground">×</span>
        <span className={`transition-all duration-300 ${hoveredIndex === 1 ? `${categories[1].color} font-bold scale-110` : 'text-muted-foreground'}`}>
          Perspectives
        </span>
        <span className="text-muted-foreground">×</span>
        <span className={`transition-all duration-300 ${hoveredIndex === 2 ? `${categories[2].color} font-bold scale-110` : 'text-muted-foreground'}`}>
          Action
        </span>
        <span className="text-muted-foreground">=</span>
        <span className={`transition-all duration-300 ${hoveredIndex === 3 ? `${categories[3].color} font-bold scale-110` : 'text-muted-foreground'}`}>
          Success
        </span>
      </span>
    );
  };

  return (
    <section id="categories" className="py-12 px-0 sm:px-4 relative">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-5xl mx-auto text-center mb-12 sm:mb-16 space-y-4 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold">
            {t("categories.heading") === "Systemische Handlungskompetenz" ? (
              <>
                <span className="md:hidden">Systemische Handlungs&shy;kompetenz</span>
                <span className="hidden md:inline">{t("categories.heading")}</span>
              </>
            ) : (
              t("categories.heading")
            )}
          </h2>
          <div className="text-xl text-muted-foreground">
            <p className="mb-8 text-lg max-w-[56rem] mx-auto leading-relaxed">
              {t("categories.intro")}
            </p>

            <div className="my-8">
              {renderEquation()}
            </div>

            <p className="mt-2 text-base max-w-2xl mx-auto">
              {language === "de" ? "Erkunden Sie die folgenden Kategorien, um mehr zu erfahren." : "Explore the categories below to learn more."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full max-w-6xl mx-auto">
          {categories.map((category, index) => (
            <div
              key={category.title}
              className="animate-scale-in"
              style={{ animationDelay: `${category.delay}ms` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <CategoryCard {...category} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Categories;
